import { HttpClient, HttpResponse } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { Subject, Observable, exhaustMap, tap, catchError, map, of, switchMap, from } from 'rxjs';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { LocalStorage } from 'src/app/shared/providers/local-storage/local-storage';
import { NetworkProvider } from 'src/app/shared/providers/network/network';
import { Storage } from '@ionic/storage';
import { EmplacementSite } from '../../models/emplacementSite';
import { CrtHttpErrorHandlerService } from '../../providers/http-error-handler.service';
import { servicesUrl } from '../../config';
import { BlocageSiteFilterEnum } from '../../models/blocageSite';

export const SITES_MAX_NBRE = 199; //(200 sites) Range de 0 à 199
/**
 * Service des Sites CRT
 */
@Injectable({
  providedIn: 'root',
})
export class SiteService extends LocalStorage {
  public readonly BLOCAGE_DELAY = 2000;

  /**
   * Clef de stockage du site selectionné
   * @type {string}
   */
  public static readonly CURRENT_SITE_STORAGE_KEY = 'crt-currentSite';
  /**
   * clef de stockage des sites du perimetre
   * @type {string}
   */
  private readonly SITES_STORAGE_KEY = 'crt-sitesPerimetre';
  /**
   * clef de stockage permettant de savoir si les sites
   * en local storage sont un résultat partiel ( > 100 sites en BDD)
   * @type {boolean}
   */
  private readonly PARTIAL_RESULT_STORAGE_KEY = 'crt-sitesPerimetrePartial';

  /**
   * Event pour mettre à jour la vue
   */
  public forceRefreshSitesFavorisInView = new Subject<void>();

  /**
   * Subject si mise à jour du site
   */
  public siteSubject$: Subject<EmplacementSite>;

  /**
   * Constructeur
   * @param {NetworkProvider} network
   * @param {Storage} storage
   * @param {HttpClient} http
   * @param crtHttpErrorHandler
   */
  constructor(
    protected override network: NetworkProvider,
    protected override storage: Storage,
    private http: HttpClient,
    private crtHttpErrorHandler: CrtHttpErrorHandlerService
  ) {
    super();
    this.serverRequestPeriod = 720;
    this.siteSubject$ = new Subject<EmplacementSite>();
  }

  public getSiteIdByDkCode(dkCodeSite: string): Observable<{
    identifiant: number;
  }> {
    const url = servicesUrl.getSiteIdByDkCode(dkCodeSite);
    return this.http
      .get<
        {
          identifiant: number;
        }[]
      >(url)
      .pipe(map((id) => id[0]));
  }

  /**
   * Http request to find an site
   * @param dkCode
   * @param forceRefresh
   * @param storageKey Permet de spécifier dans quel storage mettre à jour le site par défaut : {{this.SITES_STORAGE_KEY}}
   * @returns {Observable<EmplacementSite>}
   */
  public getSiteByIdSite(
    idSite: number,
    forceRefresh = false,
    storageKey = this.SITES_STORAGE_KEY
  ): Observable<EmplacementSite> {
    const url = servicesUrl.getSiteByIdSite({ idSite });

    return this.shouldUseWebservice(storageKey, forceRefresh, 10).pipe(
      exhaustMap((shouldUseWebservice) => {
        if (shouldUseWebservice) {
          return this.http.get<EmplacementSite>(url).pipe(
            tap(async (site) => {
              const sites = (await this.storage.get(storageKey)) || [];
              await this.storage.set(storageKey, [
                ...sites.filter(({ identifiant }) => identifiant !== site.identifiant),
                site,
              ]);
              await this.storage.set(SiteService.CURRENT_SITE_STORAGE_KEY, site);
              await this.setLastRequestDateToNow(storageKey);
              this.siteSubject$.next(site);
            }),
            catchError(async (e) => {
              await this.crtHttpErrorHandler.handleError(e);
              return (await this.storage.get(storageKey)).filter(({ identifiant }) => idSite === identifiant)[0];
            })
          );
        } else {
          return fromPromise(this.storage.get(storageKey)).pipe(
            map(
              (sites: EmplacementSite[]) => sites.filter(({ identifiant }) => idSite === identifiant)[0] // on récupère uniquement le site
            )
          );
        }
      })
    );
  }

  /**
   * Recupère la liste des sites du perimetre
   */
  public getSites(
    searchKeyword: string,
    forceServerRequest: boolean,
    blocage?: BlocageSiteFilterEnum,
    discordant?: boolean
  ): Observable<HttpResponse<EmplacementSite[]>> {
    let httpResponse = new HttpResponse<EmplacementSite[]>();
    return this.shouldUseWebservice(this.SITES_STORAGE_KEY, forceServerRequest).pipe(
      exhaustMap(async (shouldUseWebservice) => {
        let useWebService: boolean;
        if (shouldUseWebservice) {
          useWebService = true;
        } else {
          useWebService = this.network.isConnected();
        }
        return useWebService;
      }),
      exhaustMap((shouldUseWebservice) => {
        if (shouldUseWebservice) {
          const url = servicesUrl.getSiteInPerimetre;
          return this.http
            .get<EmplacementSite[]>(url, {
              observe: 'response',
              params: {
                range: `0-${SITES_MAX_NBRE}`,
                q: searchKeyword || '',
                blocage: blocage != null ? blocage : 'TOUS',
                discordant: discordant !== null ? discordant.toString() : '',
              },
            })
            .pipe(
              map((httpResponse: HttpResponse<EmplacementSite[]>) => {
                return httpResponse;
              }),
              tap(async (httpResponse: HttpResponse<EmplacementSite[]>) => {
                await this.storage.set(this.SITES_STORAGE_KEY, httpResponse.body);
                await this.storage.set(this.PARTIAL_RESULT_STORAGE_KEY, httpResponse.status === 206);
                await this.setLastRequestDateToNow(this.SITES_STORAGE_KEY);
              }),
              catchError(async (e) => {
                await this.crtHttpErrorHandler.handleError(e);
                return httpResponse.clone({
                  body: this.sortSites((await this.storage.get(this.SITES_STORAGE_KEY)) || []),
                  status: (await this.storage.get(this.PARTIAL_RESULT_STORAGE_KEY)) ? 206 : 200,
                });
              })
            );
        } else {
          return fromPromise(this.storage.get(this.PARTIAL_RESULT_STORAGE_KEY)).pipe(
            exhaustMap((isPartialResult) => {
              return fromPromise<EmplacementSite[]>(this.storage.get(this.SITES_STORAGE_KEY)).pipe(
                map((sites) => sites || []),
                map((sites: EmplacementSite[]) =>
                  httpResponse.clone({
                    body: this.sortSites(sites),
                    status: isPartialResult ? 206 : 200,
                  })
                )
              );
            })
          );
        }
      })
    );
  }

  /**
   * Supprime toutes les clefs du storage ayant le pattern "crt-${idSite}-${.+}-${LAST_REQUEST_DATE_SUFFIX}"
   * @param {number} idSite
   * @returns {Promise<void>}
   */
  public async removeAllLastRequestDateForIdSite(idSite: number) {
    const keys = await this.storage.keys();
    const siteKeysRemoved = keys
      .filter((k) => RegExp(new RegExp(`crt-${idSite}-.+${this.LAST_REQUEST_DATE_SUFFIX}`)).exec(k))
      .map((k) => this.storage.remove(k));
    await Promise.all(siteKeysRemoved);
  }

  /**
   * Supprime toutes les clefs du storage pour le site
   * @param {number} siteId
   * @returns {Promise<void>}
   */
  public async removeAllDataForSiteId(siteId: number) {
    const keys = await this.storage.keys();
    const siteKeysRemoved = keys
      .filter((k) => RegExp(new RegExp(`crt-${siteId}-.+`)).exec(k))
      .map((k) => this.storage.remove(k));
    await Promise.all(siteKeysRemoved);
  }

  /**
   * Tries une liste de sites
   * Par dkCodeSite
   * Par libelle
   */
  public sortSites(sites: EmplacementSite[]): EmplacementSite[] {
    return sites.sort((a, b) => {
      if (a.dkCodeSite === b.dkCodeSite) {
        return a.libelle.localeCompare(b.libelle);
      } else {
        return a.dkCodeSite.localeCompare(b.dkCodeSite);
      }
    });
  }

  /**
   * rechercher des sites
   * @param searchKeyword
   * @returns {Observable<EmplacementSite[]>}
   */
  public searchAllSites(
    searchKeyword: string,
    forceSearch: boolean,
    blocage?: BlocageSiteFilterEnum,
    discordant?: boolean
  ): Observable<HttpResponse<EmplacementSite[]>> {
    const url = servicesUrl.searchSite;

    return of(forceSearch).pipe(
      switchMap((useWebService) => {
        const httpResponse = new HttpResponse<EmplacementSite[]>();
        if (!searchKeyword) {
          return of(httpResponse.clone({ body: [] }));
        }
        if (useWebService) {
          return this.http.get<EmplacementSite[]>(url, {
            observe: 'response',
            params: {
              range: `0-${SITES_MAX_NBRE}`,
              q: searchKeyword,
              blocage: blocage != null ? blocage : 'TOUS',
              discordant: discordant !== null ? discordant.toString() : '',
            },
          });
        }
        return of(httpResponse.clone({ body: null }));
      }),
      catchError(async (err) => {
        const httpResponse = new HttpResponse<EmplacementSite[]>();
        await this.crtHttpErrorHandler.handleError(err);
        return httpResponse.clone({ body: [] });
      })
    );
  }
}
